import React, { useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import AddRoundedIcon from "@material-ui/icons/AddRounded";
import CloudUploadRoundedIcon from "@material-ui/icons/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@material-ui/icons/DeleteOutlineRounded";
import ImageRoundedIcon from "@material-ui/icons/ImageRounded";
import Inventory2RoundedIcon from "@material-ui/icons/StorefrontRounded";

import { toast } from "react-toastify";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { useSetup } from "../hooks/useSetup";

import {
  KoraxOnboardingShell,
  KoraxOnboardingContent,
  KoraxOnboardingFooter
} from "../../../components/KoraxUI/Onboarding";

const useStyles = makeStyles(theme => ({
  layout: {
    width: "100%",
    maxWidth: 940,
    display: "grid",
    gridTemplateColumns: "1fr .85fr",
    gap: 18,

    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    }
  },

  panel: {
    padding: 22,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF"
  },

  title: {
    fontSize: 16,
    fontWeight: 800,
    color: theme.palette.text.primary
  },

  subtitle: {
    marginTop: 6,
    marginBottom: 18,
    fontSize: 12.5,
    lineHeight: 1.5,
    color: theme.palette.text.secondary
  },

  field: {
    marginBottom: 14,

    "& .MuiOutlinedInput-root": {
      borderRadius: 12
    }
  },

  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  upload: {
    minHeight: 120,
    marginBottom: 14,
    padding: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    borderRadius: 14,
    border: "1px dashed rgba(21,94,239,.4)",
    background: "rgba(21,94,239,.04)",
    textAlign: "center",
    cursor: "pointer",
    overflow: "hidden"
  },

  uploadImage: {
    width: "100%",
    height: 150,
    objectFit: "cover",
    borderRadius: 12
  },

  uploadIcon: {
    fontSize: 34,
    color: "#155EEF",
    marginBottom: 6
  },

  hiddenInput: {
    display: "none"
  },

  saveButton: {
    minHeight: 44,
    borderRadius: 12,
    fontWeight: 800,
    textTransform: "none"
  },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: 10
  },

  item: {
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`
  },

  itemImage: {
    width: 52,
    height: 52,
    flexShrink: 0,
    objectFit: "cover",
    borderRadius: 10,
    background: "rgba(21,94,239,.08)"
  },

  itemPlaceholder: {
    width: 52,
    height: 52,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    background: "rgba(21,94,239,.08)",
    color: "#155EEF"
  },

  itemContent: {
    minWidth: 0,
    flex: 1
  },

  itemName: {
    fontSize: 14,
    fontWeight: 800,
    color: theme.palette.text.primary
  },

  itemDescription: {
    marginTop: 3,
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  empty: {
    minHeight: 210,
    padding: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    borderRadius: 14,
    border: `1px dashed ${theme.palette.divider}`,
    textAlign: "center",
    color: theme.palette.text.secondary
  },

  emptyIcon: {
    marginBottom: 10,
    fontSize: 38,
    color: "#155EEF"
  },

  price: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 700,
    color: "#155EEF"
  },

  info: {
    width: "100%",
    maxWidth: 940,
    marginTop: 18,
    padding: 14,
    borderRadius: 12,
    background: "rgba(21,94,239,.06)"
  }
}));

const emptyForm = {
  type: "product",
  name: "",
  description: "",
  price: "",
  imageUrl: ""
};

export default function CatalogStep() {
  const classes = useStyles();
  const { nextStep, previousStep } = useSetup();

  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(true);

  const set = (key, value) => {
    setForm(previous => ({
      ...previous,
      [key]: value
    }));
  };

  const loadItems = async () => {
    try {
      setLoadingItems(true);
      const { data } = await api.get("/catalog-items");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const uploadImage = async event => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Envie apenas arquivos de imagem.");
      event.target.value = "";
      return;
    }

    try {
      setUploading(true);

      const dataFile = new FormData();
      dataFile.append("file", file);

      const { data } = await api.post(
        "/catalog-items/upload-image",
        dataFile,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      set("imageUrl", data.imageUrl);
      toast.success("Imagem adicionada.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const saveItem = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome do produto ou serviço.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        type: form.type,
        name: form.name.trim(),
        description: form.description.trim(),
        price: form.price ? Number(String(form.price).replace(",", ".")) : 0,
        imageUrl: form.imageUrl || null,
        active: true,
        featured: false,
        ctaText: "Tenho interesse"
      };

      const { data } = await api.post("/catalog-items", payload);

      setItems(previous => [data, ...previous]);
      setForm(emptyForm);
      toast.success("Item adicionado ao catálogo.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async item => {
    try {
      await api.delete(`/catalog-items/${item.id}`);

      setItems(previous =>
        previous.filter(currentItem => currentItem.id !== item.id)
      );

      toast.success("Item removido.");
    } catch (err) {
      toastError(err);
    }
  };

  const formatPrice = value => {
    const numericValue = Number(value || 0);

    if (!numericValue) {
      return "Preço não informado";
    }

    return numericValue.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    });
  };

  return (
    <KoraxOnboardingShell step={6} totalSteps={9}>
      <KoraxOnboardingContent
        eyebrow="Catálogo comercial"
        title="Cadastre seus produtos ou serviços"
        description="Esses itens serão adicionados diretamente ao catálogo oficial da Korax e poderão ser usados pela sua IA durante as conversas."
      >
        <div className={classes.layout}>
          <div className={classes.panel}>
            <Typography className={classes.title}>
              Adicionar item
            </Typography>

            <Typography className={classes.subtitle}>
              Cadastre ao menos um produto ou serviço. Você poderá editar ou adicionar novos itens depois.
            </Typography>

            <TextField
              select
              fullWidth
              variant="outlined"
              className={classes.field}
              label="Tipo"
              value={form.type}
              onChange={event => set("type", event.target.value)}
            >
              <MenuItem value="product">Produto</MenuItem>
              <MenuItem value="service">Serviço</MenuItem>
              <MenuItem value="combo">Combo</MenuItem>
            </TextField>

            <TextField
              required
              fullWidth
              variant="outlined"
              className={classes.field}
              label="Nome"
              placeholder="Ex.: Aliança de ouro 18k"
              value={form.name}
              onChange={event => set("name", event.target.value)}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              className={classes.field}
              label="Descrição"
              placeholder="Descreva as principais características, benefícios ou informações importantes."
              value={form.description}
              onChange={event => set("description", event.target.value)}
            />

            <TextField
              fullWidth
              variant="outlined"
              className={classes.field}
              label="Preço"
              placeholder="Ex.: 149,90"
              value={form.price}
              onChange={event => set("price", event.target.value)}
            />

            <input
              id="catalog-onboarding-image"
              type="file"
              accept="image/*"
              className={classes.hiddenInput}
              onChange={uploadImage}
            />

            <label htmlFor="catalog-onboarding-image">
              <div className={classes.upload}>
                {form.imageUrl ? (
                  <img
                    src={form.imageUrl}
                    alt="Produto"
                    className={classes.uploadImage}
                  />
                ) : (
                  <>
                    {uploading ? (
                      <CircularProgress size={30} />
                    ) : (
                      <CloudUploadRoundedIcon className={classes.uploadIcon} />
                    )}

                    <Typography variant="body2">
                      {uploading
                        ? "Enviando imagem..."
                        : "Clique para adicionar uma imagem"}
                    </Typography>
                  </>
                )}
              </div>
            </label>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              className={classes.saveButton}
              startIcon={
                saving ? (
                  <CircularProgress size={18} />
                ) : (
                  <AddRoundedIcon />
                )
              }
              disabled={saving || uploading || !form.name.trim()}
              onClick={saveItem}
            >
              {saving ? "Adicionando..." : "Adicionar ao catálogo"}
            </Button>
          </div>

          <div className={classes.panel}>
            <Typography className={classes.title}>
              Itens cadastrados
            </Typography>

            <Typography className={classes.subtitle}>
              {items.length === 0
                ? "Nenhum item foi cadastrado ainda."
                : `${items.length} item(ns) disponível(is) no catálogo.`}
            </Typography>

            {loadingItems ? (
              <div className={classes.empty}>
                <CircularProgress size={32} />
              </div>
            ) : items.length === 0 ? (
              <div className={classes.empty}>
                <Inventory2RoundedIcon className={classes.emptyIcon} />

                <Typography variant="body2">
                  Seu catálogo ainda está vazio.
                </Typography>
              </div>
            ) : (
              <div className={classes.list}>
                {items.map(item => (
                  <div className={classes.item} key={item.id}>
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className={classes.itemImage}
                      />
                    ) : (
                      <div className={classes.itemPlaceholder}>
                        <ImageRoundedIcon />
                      </div>
                    )}

                    <div className={classes.itemContent}>
                      <Typography className={classes.itemName}>
                        {item.name}
                      </Typography>

                      <Typography className={classes.itemDescription}>
                        {item.description || "Sem descrição"}
                      </Typography>

                      <Typography className={classes.price}>
                        {formatPrice(item.price)}
                      </Typography>
                    </div>

                    <IconButton
                      size="small"
                      onClick={() => removeItem(item)}
                    >
                      <DeleteOutlineRoundedIcon />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={classes.info}>
          <Typography variant="body2">
            O cadastro é opcional, mas adicionar produtos agora ajuda a Korax a começar com informações comerciais mais completas.
          </Typography>
        </div>
      </KoraxOnboardingContent>

      <KoraxOnboardingFooter
        onBack={previousStep}
        onNext={nextStep}
        nextLabel="Continuar"
      />
    </KoraxOnboardingShell>
  );
}
